/**
 * Export Value Formatters
 *
 * Defines formatting overrides for benefit fields that emit complex values
 * (arrays of objects, nested structures) which AgGrid's default export
 * cannot handle — resulting in "[object Object]" or raw JSON in the xlsx.
 *
 * Each entry maps a coverage type to a set of benefit keys, each with:
 * - `formatter`: Converts the raw value to a human-readable string
 * - `condition` (optional): A function that checks the previous row's value
 *   in the same column to determine whether formatting should be applied.
 *   When omitted, the formatter is always applied.
 *
 * The "previous row" check works because in the grid's row order, a type
 * field always directly precedes its associated value/details field.
 */

import { SERVICE_TYPE_LABELS } from "@platform/plan-grid-core";

/**
 * Formats a raw cell value into a string suitable for Excel export.
 * Should return the original value as a string if it cannot be formatted.
 */
export type ExportValueFormatter = (value: unknown) => string;

/**
 * Checks whether the formatter should be applied, based on the previous
 * row's value in the same column (typically a "type" field).
 *
 * @param previousRowValue - The value from the row directly above in the same column
 * @returns true if the formatter should be applied
 */
export type ExportCondition = (previousRowValue: unknown) => boolean;

export interface ExportFormatterEntry {
  formatter: ExportValueFormatter;
  condition?: ExportCondition;
}

type ExportFormattersMap = Record<string, Record<string, ExportFormatterEntry>>;

// ---------------------------------------------------------------------------
// Value entry shapes
// ---------------------------------------------------------------------------

interface AgeReductionEntry {
  reductionPercent?: number;
  age?: number;
}

interface GuaranteeIssueAgeEntry {
  amount?: number;
  ageLower?: number;
  ageUpper?: number;
}

interface AmountEntry {
  amount?: number;
}

interface MultiOptionEntry {
  type?: string;
  value?: number;
}

interface ServiceWaitingPeriodEntry {
  type?: string;
  months?: number;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const isNonEmptyArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.length > 0;

// ---------------------------------------------------------------------------
// Reusable formatters
// ---------------------------------------------------------------------------

/**
 * Age Reduction Schedule: `[{reductionPercent, age}, ...]`
 * Renders as "50% at 65, 75% at 70"
 */
export const formatAgeReductionSchedule: ExportValueFormatter = (
  value: unknown,
): string => {
  if (!isNonEmptyArray(value)) return "";
  return (value as AgeReductionEntry[])
    .map((entry) => {
      const percent = entry.reductionPercent ?? "";
      const age = entry.age ?? "";
      return `${percent}% at ${age}`;
    })
    .join(", ");
};

/**
 * Guarantee Issue Age-Based Amounts: `[{amount, ageLower, ageUpper}, ...]`
 * Renders as "$100,000 at ages 18-65, $50,000 at ages 66-70"
 */
export const formatGuaranteeIssueAges: ExportValueFormatter = (
  value: unknown,
): string => {
  if (!isNonEmptyArray(value)) return "";
  return (value as GuaranteeIssueAgeEntry[])
    .map((entry) => {
      const amount = entry.amount != null ? formatCurrency(entry.amount) : "";
      const lower = entry.ageLower ?? "";
      const upper = entry.ageUpper ?? "";
      return `${amount} at ages ${lower}-${upper}`;
    })
    .join(", ");
};

/**
 * Multiple Dollar Amounts: `[{amount}, ...]`
 * Renders as "$5,000, $10,000, $25,000"
 *
 * Covers multipleAmounts, multipleDollarAmountsPopover, and
 * benefitDescriptionMultiOptionsPopover when entries have type "dollarAmount".
 */
export const formatMultipleDollarAmounts: ExportValueFormatter = (
  value: unknown,
): string => {
  if (!isNonEmptyArray(value)) return "";
  return (value as AmountEntry[])
    .map((entry) => (entry.amount != null ? formatCurrency(entry.amount) : ""))
    .filter(Boolean)
    .join(", ");
};

/**
 * Multiple Salary Multipliers: `[{amount}, ...]`
 * Renders as "2x salary, 3x salary"
 */
export const formatMultipleSalaryMultipliers: ExportValueFormatter = (
  value: unknown,
): string => {
  if (!isNonEmptyArray(value)) return "";
  return (value as AmountEntry[])
    .map((entry) => (entry.amount != null ? `${entry.amount}x salary` : ""))
    .filter(Boolean)
    .join(", ");
};

/**
 * Benefit Description Multi-Options: `[{type, value}, ...]`
 * Renders as "$5,000, $10,000, 2x salary" — mixed dollar and salary entries
 */
export const formatBenefitDescriptionMultiOptions: ExportValueFormatter = (
  value: unknown,
): string => {
  if (!isNonEmptyArray(value)) return "";
  return (value as MultiOptionEntry[])
    .map((entry) => {
      if (entry.value == null) return "";
      if (entry.type === "dollarAmount") return formatCurrency(entry.value);
      if (entry.type === "salaryMultiplier") return `${entry.value}x salary`;
      return String(entry.value);
    })
    .filter(Boolean)
    .join(", ");
};

/**
 * Service Waiting Periods (type labels): `[{type, months}, ...]`
 * Renders as "Basic, Major, Ortho"
 */
export const formatServiceWaitingPeriodTypes: ExportValueFormatter = (
  value: unknown,
): string => {
  if (!Array.isArray(value)) return "";
  if (value.length === 0) return "None";
  return (value as ServiceWaitingPeriodEntry[])
    .map(
      (entry) =>
        SERVICE_TYPE_LABELS[entry.type as keyof typeof SERVICE_TYPE_LABELS] ??
        entry.type ??
        "",
    )
    .filter(Boolean)
    .join(", ");
};

/**
 * Service Waiting Periods (months): `[{type, months}, ...]`
 * Renders as "6, 12, 12"
 */
export const formatServiceWaitingPeriodMonths: ExportValueFormatter = (
  value: unknown,
): string => {
  if (!isNonEmptyArray(value)) return "-";
  return (value as ServiceWaitingPeriodEntry[])
    .map((entry) => entry.months ?? "")
    .filter((v) => v !== "")
    .join(", ");
};

// ---------------------------------------------------------------------------
// Reusable conditions
// ---------------------------------------------------------------------------

/**
 * Creates a condition that passes when the previous row's value matches
 * any of the given type strings.
 */
const whenTypeIsOneOf =
  (...types: string[]): ExportCondition =>
  (previousRowValue: unknown): boolean =>
    typeof previousRowValue === "string" && types.includes(previousRowValue);

const whenIsNotUndefined: ExportCondition = (
  previousRowValue: unknown,
): boolean => previousRowValue !== undefined;

// ---------------------------------------------------------------------------
// Reusable entry builders
// ---------------------------------------------------------------------------

const ageReductionScheduleEntry: ExportFormatterEntry = {
  formatter: formatAgeReductionSchedule,
  condition: whenTypeIsOneOf("percentageAtAge"),
};

/**
 * Builds an entry for a field that can hold dollar amounts, salary
 * multipliers, or mixed multi-options depending on the type value.
 *
 * Inspects the value shape to pick the right formatter:
 * - Entries with `{type, value}` use the multi-options formatter (mixed)
 * - All other arrays use the dollar amounts formatter by default
 */
const multiVariantAmountEntry = (
  dollarTriggers: string[],
  salaryTriggers: string[],
  multiOptionsTriggers: string[] = [],
): ExportFormatterEntry => {
  const allTriggers = [
    ...dollarTriggers,
    ...salaryTriggers,
    ...multiOptionsTriggers,
  ];

  return {
    formatter: (value: unknown): string => {
      if (!isNonEmptyArray(value)) return "";

      const first = value[0] as Record<string, unknown>;

      // Multi-options entries have {type, value} — mixed dollar and salary
      if ("type" in first && "value" in first) {
        return formatBenefitDescriptionMultiOptions(value);
      }

      // For {amount} entries we can't distinguish dollar vs salary from
      // shape alone. However the condition already gates *when* this
      // formatter runs, so we check the triggers to pick the right one.
      // At runtime the condition has already matched, but the formatter
      // doesn't receive the type — so we sniff the value range heuristic:
      // salary multipliers are typically small numbers (< 50).
      // This is imperfect, so we split salary-only triggers into separate
      // map entries where possible. This branch is the fallback.
      return formatMultipleDollarAmounts(value);
    },
    condition: whenTypeIsOneOf(...allTriggers),
  };
};

/**
 * Builds a dollar-only amount entry for fields whose array values are
 * always `[{amount}]` formatted as currency.
 */
const dollarAmountsEntry = (...triggers: string[]): ExportFormatterEntry => ({
  formatter: formatMultipleDollarAmounts,
  condition: whenTypeIsOneOf(...triggers),
});

// ---------------------------------------------------------------------------
// Formatter map
// ---------------------------------------------------------------------------

export const exportValueFormatters: ExportFormattersMap = {
  // ============================
  // Basic Life / AD&D
  // ============================
  basicLifeAdnd: {
    ageReductionScheduleSchedules: ageReductionScheduleEntry,
  },

  // ============================
  // Voluntary Life / AD&D
  // ============================
  voluntaryLifeAdnd: {
    ageReductionScheduleSchedules: ageReductionScheduleEntry,

    // Employee benefit description — can be multipleOptions,
    // multipleDollarAmounts, or multipleSalaryMultipliers
    benefitDescriptionEmployeeAmount: multiVariantAmountEntry(
      ["multipleOptions", "multipleDollarAmounts"],
      ["multipleSalaryMultipliers"],
    ),

    // Spouse benefit description — the stored type can be either the raw
    // API "multipleOptions" enum (legacy/carrier-imported data) or the
    // frontend "multipleDollarAmounts" sentinel (data saved through this
    // grid's own type dropdown, which only ever writes the sentinel) — both
    // need to trigger the same multi-amount formatting.
    benefitDescriptionSpouseAmount: dollarAmountsEntry(
      "multipleOptions",
      "multipleDollarAmounts",
    ),

    // Child benefit descriptions (3 age ranges) — same dual trigger as spouse.
    benefitDescriptionChildDependentAgesBirthTo14DaysAmount: dollarAmountsEntry(
      "multipleOptions",
      "multipleDollarAmounts",
    ),
    benefitDescriptionChildDependentAges15DaysTo6MonthsAmount:
      dollarAmountsEntry("multipleOptions", "multipleDollarAmounts"),
    benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount:
      dollarAmountsEntry("multipleOptions", "multipleDollarAmounts"),
  },

  // ============================
  // Critical Illness
  // ============================
  criticalIllness: {
    ageReductionScheduleSchedules: ageReductionScheduleEntry,

    // Employee benefit description — can be the raw API "multipleOptions"
    // enum (Build's own row data, unremapped) or the frontend
    // "multipleDollarAmounts"/"multipleSalaryMultipliers" sentinels.
    employeeBenefitDescriptionAmount: multiVariantAmountEntry(
      ["multipleOptions", "multipleDollarAmounts"],
      ["multipleSalaryMultipliers"],
    ),

    // Spouse benefit description — same triggers
    spouseBenefitDescriptionAmount: multiVariantAmountEntry(
      ["multipleOptions", "multipleDollarAmounts"],
      ["multipleSalaryMultipliers"],
    ),

    // Child benefit description — flatAmount triggers multipleDollarAmountsPopover
    childBenefitDescriptionAmount: dollarAmountsEntry("flatAmount"),
  },

  // ============================
  // Accident
  // ============================
  accident: {
    ageReductionScheduleSchedules: ageReductionScheduleEntry,
  },

  // ============================
  // Whole Life
  // ============================
  wholeLife: {
    // Employee benefit amount — multipleOptions, multipleDollarAmounts,
    // or multipleSalaryMultipliers
    employeeBenefitAmountAmount: multiVariantAmountEntry(
      ["multipleOptions", "multipleDollarAmounts"],
      ["multipleSalaryMultipliers"],
    ),
  },

  // ============================
  // Dental HMO
  // ============================
  "dental-hmo": {
    serviceWaitingPeriods: {
      formatter: formatServiceWaitingPeriodTypes,
    },
    serviceWaitingPeriodsMonths: {
      formatter: formatServiceWaitingPeriodMonths,
    },
  },

  // ============================
  // Dental PPO
  // ============================
  "dental-ppo": {
    serviceWaitingPeriods: {
      formatter: formatServiceWaitingPeriodTypes,
      condition: whenIsNotUndefined,
    },
    serviceWaitingPeriodsMonths: {
      formatter: formatServiceWaitingPeriodMonths,
      condition: whenIsNotUndefined,
    },
  },
};
